import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import ReCaptchaEnterprise from './recaptcha';

const questionOptions = [
  {
    label: 'For',
    value: '100',
  },
  {
    label: 'Against',
    value: '010',
  },
  {
    label: 'Neutral',
    value: '001',
  },
];

const ownerOptions = [
  {
    label: 'Yes',
    value: 1,
  },
  {
    label: 'No',
    value: 0,
  },
];

const FeedbackSelect = ({
  label = '',
  name,
  onChange,
  options,
  style,
  value,
}) => (
  <FeedbackSelect.Control style={style}>
    <InputLabel htmlFor={name}>{label}</InputLabel>
    <Select
      inputProps={{ id: name, name }}
      onChange={(event) => onChange(event.target.value)}
      value={value}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  </FeedbackSelect.Control>
);

FeedbackSelect.Control = styled(FormControl)`
  min-width: inherit;
  width: 100%;
  margin-right: 20px !important;
`;

const FeedbackTextField = ({ onChange, ...textFieldProps }) => (
  <FeedbackTextField.Wrapper>
    <TextField
      onChange={(event) => onChange(event.target.value)}
      {...textFieldProps}
    />
  </FeedbackTextField.Wrapper>
);

FeedbackTextField.Wrapper = styled.div`
  position: relative;
  margin-bottom: 15px;

  > div {
    width: 100%;
  }
`;

const styles = () => ({
  dialogPaper: {
    overflow: 'inherit',
    width: '95vw',
    maxWidth: 800,
  },
  dialogActions: {
    justifyContent: 'center',
    flexWrap: 'wrap',
    maxHeight: 100,
    '&> div': {
      maxHeight: 100,
    },
  },
  button: {
    background: '#3d3f72',
    color: '#fff',
    '&:disabled': {
      background: '#ddd',
      color: '#3d3f7255',
    },
    '&:hover': {
      background: '#3d3f72DD',
      color: '#fff',
    },
    marginTop: 10,
    marginBottom: 10,
  },
});

const FeedbackPanel = ({
  classes,
  handleSubmit,
  fetchTaxingUnitPollQuestions,
  unitId,
  year,
  pushToast,
}) => {
  const [comment, setComment] = useState('');
  const [isOwner, setIsOwner] = useState('');
  const [captchaOk, setCaptchaOk] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [isLoadQuestion, setLoadedQuestion] = useState(null);
  const [responses, setResponses] = useState({});
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const isDisabled =
    !captchaOk || isOwner === '' || !comment || !email || !phone;

  const fetchPollQuestions = async () => {
    try {
      if (unitId) {
        setLoadedQuestion(true);
        const res = await fetchTaxingUnitPollQuestions({ id: unitId, year });
        setLoadedQuestion(false);
        if (res) {
          setQuestions(res.results);
        } else {
          setQuestions([]);
        }
      }
    } catch (e) {
      pushToast({ error: e, entity: 'Fetching Feedback Questions' });
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: parents pass fetchTaxingUnitPollQuestions via .bind() on every render; refetch only when unitId/year change
  useEffect(() => {
    fetchPollQuestions();
  }, [unitId, year]);

  return (
    <div>
      <FeedbackPanel.Title as='h3'>
        Provide Feedback to Your Taxing Authority
      </FeedbackPanel.Title>
      <form
        onSubmit={async (ev) => {
          ev.preventDefault();
          setSubmitStatus('');
          try {
            const mapedQuestions = Object.entries(responses).reduce(
              (acc, [questionID, value]) => {
                acc.push({
                  questionID,
                  response: {
                    responseFor: Number(value[0]),
                    responseAgainst: Number(value[1]),
                    responseNeutral: Number(value[2]),
                  },
                });

                return acc;
              },
              [],
            );

            handleSubmit({
              message: comment,
              isOwner,
              contactPhone: phone,
              contactEmail: email,
              questions: mapedQuestions,
            });
            setSubmitStatus('Feedback submitted successfully.');
          } catch (e) {
            setSubmitStatus(
              'There was an error submitting your feedback. Please try again.',
            );
            pushToast({ error: e, entity: 'Sending Feedback Questions' });
          }
        }}
      >
        <div style={{ background: '#e2e2ff', padding: '10px 20px' }}>
          <Typography gutterBottom>
            As part of the new Texas tax transparency laws, we are providing the
            ability for taxpayers to submit comments to their taxing units. Take
            advantage of this by providing input in regards to adopted budgets,
            hearings, tax rates, and any other topic regarding property taxes in
            your area.
          </Typography>
          <Typography gutterBottom>
            We ask that all comments remain professional and courteous. Upon
            submission, your comment will be forwarded to taxing unit
            representatives.
          </Typography>
        </div>
        <div>
          <FeedbackPanel.Title as='h4'>Comment</FeedbackPanel.Title>
          <FeedbackPanel.InputWrapper>
            <FeedbackTextField
              InputLabelProps={{
                htmlFor: 'feedback-comment',
                style: { color: '#5c5c66' },
              }}
              inputProps={{ id: 'feedback-comment' }}
              label='Type your comment here...'
              multiline
              onChange={setComment}
              value={comment}
              variant='outlined'
            />
          </FeedbackPanel.InputWrapper>
          <FeedbackPanel.Title as='h4'>Feedback Questions</FeedbackPanel.Title>
          {isLoadQuestion ? (
            <div role='status' style={{ textAlign: 'center' }}>
              <CircularProgress
                aria-label='Loading feedback questions'
                style={{ marginTop: 15, display: 'inline-block' }}
              />
            </div>
          ) : (
            <FeedbackPanel.Questions>
              {questions.map(({ question, taxingUnitPollQuestionID }, i) => (
                <FeedbackPanel.Question key={taxingUnitPollQuestionID}>
                  <FeedbackPanel.li $i={i + 1}>{question}</FeedbackPanel.li>
                  <FeedbackSelect
                    label={`Response to question ${i + 1}`}
                    name={`poll-question-${taxingUnitPollQuestionID}`}
                    onChange={(value) =>
                      setResponses((v) => ({
                        ...v,
                        [taxingUnitPollQuestionID]: value,
                      }))
                    }
                    options={questionOptions}
                    style={{ marginTop: -13, width: 100 }}
                    value={responses[taxingUnitPollQuestionID] || ''}
                  />
                </FeedbackPanel.Question>
              ))}
            </FeedbackPanel.Questions>
          )}
        </div>
        <FeedbackPanel.Contacts>
          <FeedbackTextField
            InputLabelProps={{
              htmlFor: 'feedback-phone',
              style: { color: '#5c5c66' },
            }}
            inputProps={{ id: 'feedback-phone' }}
            label='Contact Phone Number'
            onChange={setPhone}
            required
            type='tel'
            value={phone}
          />
          <FeedbackTextField
            InputLabelProps={{
              htmlFor: 'feedback-email',
              style: { color: '#5c5c66' },
            }}
            inputProps={{ id: 'feedback-email' }}
            label='Contact Email'
            onChange={setEmail}
            required
            type='email'
            value={email}
          />
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <label
              htmlFor='property-owner'
              style={{ whiteSpace: 'nowrap', marginRight: 20 }}
            >
              Are you the owner of this property?
            </label>
            <FeedbackSelect
              name='property-owner'
              onChange={setIsOwner}
              options={ownerOptions}
              value={isOwner}
            />
          </div>
        </FeedbackPanel.Contacts>
        <FeedbackPanel.ButtonWrapper>
          <ReCaptchaEnterprise
            onChange={() => setCaptchaOk(true)}
            onError={() => setCaptchaOk(false)}
          />
          <div style={{ marginLeft: 20 }}>
            <Button
              className={classes.button}
              disabled={isDisabled}
              type='submit'
              variant='contained'
            >
              SEND FEEDBACK
            </Button>
          </div>
        </FeedbackPanel.ButtonWrapper>
        <div
          aria-live='polite'
          role='status'
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
          }}
        >
          {submitStatus}
        </div>
      </form>
    </div>
  );
};

FeedbackPanel.Title = styled.div`
  font-size: 15px;
  font-weight: bold;
  margin: 0 0 10px 0;
`;

FeedbackPanel.li = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  margin: 20px 0;
  word-break: break-all;

  &::before {
    content: '${(props) => props.$i}.';
    position: absolute;
    margin-left: -20px;
    align-self: flex-start;
  }

  &::after {
    content: ' ';
    flex: 1 1 50px;
    background-image: linear-gradient(to right, #333 40%, rgba(255, 255, 255, 0) 20%);
    background-position: top;
    background-size: 10px 1px;
    background-repeat: repeat-x;
    display: block;
    height: 1px;
    margin: 0 15px;
  }
`;

FeedbackPanel.Question = styled.div`
  display: flex;
`;

FeedbackPanel.Questions = styled.div`
  padding: 0 20px;
`;

FeedbackPanel.InputWrapper = styled.div`
  fieldset {
    border-color: #333fb8 !important;
    background-color: rgb(239, 239, 255);
  }
`;

FeedbackPanel.Contacts = styled.div`
  display: flex;
  padding: 16px;
  align-items: flex-start;
  flex-wrap: wrap;

  > div {
    &:last-child {
      margin-right: 20px;
    }

    flex-grow: 1;
    flex-shrink: 1;
    margin-right: 40px;
  }
`;

FeedbackPanel.ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export default withStyles(styles)(FeedbackPanel);
